import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '../../components/Button';
import { Container } from '../../components/Container';
import Content from '../../components/Content';
import { H1 } from '../../components/Heading';
import { clearStoredSession } from '../../State/Auth/session';
import type { KeycloakInitError } from '../../State/Keycloak/KeycloakProvider';
import { reloadPage } from '../../Utils/reload';
import { size } from '../../layout/helpers';

type Action = 'retry' | 'login';

interface ReasonContent {
  h1: string;
  description: string;
  hint?: string;
  action: Action;
}

/**
 * En åtgärd per orsak. "Försök igen" laddar om sidan med samma sparade
 * session och hjälper när felet var tillfälligt. "Logga in igen" rensar
 * sessionen först, så att uppstarten går via Keycloaks inloggning, och
 * hjälper när det är den sparade sessionen som inte fungerar.
 */
const reasonContent: Record<KeycloakInitError, ReasonContent> = {
  unreachable: {
    h1: 'service_unavailable_unreachable_h1',
    description: 'service_unavailable_unreachable_description',
    action: 'retry',
  },
  timeout: {
    h1: 'service_unavailable_timeout_h1',
    description: 'service_unavailable_timeout_description',
    hint: 'service_unavailable_timeout_hint',
    action: 'login',
  },
  auth: {
    h1: 'service_unavailable_auth_h1',
    description: 'service_unavailable_auth_description',
    action: 'login',
  },
};

const actions: Record<Action, { label: string; run: () => void }> = {
  retry: {
    label: 'service_unavailable_action_retry',
    run: () => reloadPage(),
  },
  login: {
    label: 'service_unavailable_action_login',
    run: () => {
      clearStoredSession();
      reloadPage();
    },
  },
};

interface ServiceUnavailableProps {
  /** Varför uppstarten misslyckades. Utan orsak visas den generella texten utan åtgärd. */
  reason?: KeycloakInitError;
}

const ServiceUnavailable = ({ reason }: ServiceUnavailableProps) => {
  const { t } = useTranslation();
  const content = reason ? reasonContent[reason] : undefined;
  const action = content ? actions[content.action] : undefined;

  return (
    <CenteredContainer $fullHeight={Boolean(reason)}>
      <AccentBar aria-hidden="true" />
      <CenteredHeading>{t(content?.h1 ?? 'service_unavailable_h1')}</CenteredHeading>
      <CenteredContent tone="alt">
        <p>{t(content?.description ?? 'service_unavailable_description')}</p>
      </CenteredContent>
      {content?.hint && (
        <Hint>
          <p>{t(content.hint)}</p>
        </Hint>
      )}
      {action && (
        <ActionWrapper>
          <Button type="button" $tone="primary" onClick={action.run}>
            {t(action.label)}
          </Button>
        </ActionWrapper>
      )}
    </CenteredContainer>
  );
};

export default ServiceUnavailable;

// Med en orsak visas sidan ensam, utan header och footer, och ska då fylla
// hela fönstret så att html-bakgrunden inte syns under innehållet.
const CenteredContainer = styled(Container)<{ $fullHeight: boolean }>`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  min-height: ${({ $fullHeight }) => ($fullHeight ? '100vh' : '60vh')};
`;

const AccentBar = styled.div`
  width: 48px;
  height: 3px;
  margin-bottom: ${size(3)};
  background-color: ${(props) => props.theme.color.brand.primary.main};
`;

const CenteredHeading = styled(H1)`
  text-align: center;
`;

const CenteredContent = styled(Content)`
  text-align: center;
  max-width: 640px;
`;

const Hint = styled.div`
  max-width: 640px;
  margin-top: ${size(3)};
  padding: ${size(2)} ${size(3)};
  border-radius: 10px;
  background-color: ${(props) => props.theme.color.bg.medium};
  color: ${(props) => props.theme.color.text.main};
  text-align: left;

  p {
    margin: 0;
  }
`;

const ActionWrapper = styled.div`
  margin-top: ${size(4)};
`;
